import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { vehicleBrands, services, stats, dealers, testimonials } from '../mock';
import { Search, Star, Zap, MapPin, Phone, Mail, ChevronRight, Users, MessageCircle, X } from 'lucide-react';
import { useSeo } from '../hooks/useSeo';
import { seo } from '../seoConfig';
import LegalLinks from '../components/LegalLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/tuning`;

const Home = () => {
  useSeo(seo.home);

  // Turkish locale suggestion banner: shows only when navigator language is
  // Turkish AND the user hasn't dismissed it in this session. Purely advisory —
  // never redirects; content below still renders fully in English.
  const [showTrBanner, setShowTrBanner] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    if (sessionStorage.getItem('trBannerDismissed') === '1') return;
    const langs = [navigator.language, ...(navigator.languages || [])]
      .filter(Boolean)
      .map((l) => l.toLowerCase());
    if (langs.some((l) => l.startsWith('tr'))) setShowTrBanner(true);
  }, []);
  const dismissTrBanner = () => {
    sessionStorage.setItem('trBannerDismissed', '1');
    setShowTrBanner(false);
  };

  // Data states
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [engines, setEngines] = useState([]);
  
  // Selection states
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedGeneration, setSelectedGeneration] = useState('');
  const [selectedEngine, setSelectedEngine] = useState('');
  
  // Loading states
  const [loading, setLoading] = useState(false);

  // Load brands on mount
  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error('Error loading brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBrandChange = async (brandId) => {
    setSelectedBrand(brandId);
    setSelectedModel('');
    setSelectedGeneration('');
    setSelectedEngine('');
    setModels([]);
    setGenerations([]);
    setEngines([]);
    
    if (brandId) {
      try {
        const response = await axios.get(`${API}/brands/${brandId}/models`);
        setModels(response.data);
      } catch (error) {
        console.error('Error loading models:', error);
      }
    }
  };

  const handleModelChange = async (modelId) => {
    setSelectedModel(modelId);
    setSelectedGeneration('');
    setSelectedEngine('');
    setGenerations([]);
    setEngines([]);
    
    if (modelId) {
      try {
        const response = await axios.get(`${API}/models/${modelId}/generations`);
        setGenerations(response.data);
      } catch (error) {
        console.error('Error loading generations:', error);
      }
    }
  };

  const handleGenerationChange = async (generationId) => {
    setSelectedGeneration(generationId);
    setSelectedEngine('');
    setEngines([]);
    
    if (generationId) {
      try {
        const response = await axios.get(`${API}/generations/${generationId}/engines`);
        setEngines(response.data);
      } catch (error) {
        console.error('Error loading engines:', error);
      }
    }
  };

  const handleSearch = async () => {
    if (selectedEngine) {
      window.location.href = `/calculator?engine=${selectedEngine}`;
    } else {
      alert('Please select an engine to see performance data');
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/905327866635?text=Hello, I would like to know more about Powerlab tuning services', '_blank');
  };

  const vehicleTypes = [
    { id: 'car', label: 'Car' },
    { id: 'moto', label: 'Moto' },
    { id: 'trucks', label: 'Trucks' },
    { id: 'jetski', label: 'Jetski' },
    { id: 'agri', label: 'Agri' },
    { id: 'atv', label: 'ATV' }
  ];

  const performanceParts = [
    {
      title: 'Engine Performance',
      description: 'Parts',
      image: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80',
      link: '/parts/engine'
    },
    {
      title: 'Performance Chassis',
      description: 'Parts',
      image: 'https://images.unsplash.com/photo-1621939514649-280e2ee25f60?w=800&q=80',
      link: '/parts/chassis'
    },
    {
      title: 'Preparation Stage 3',
      description: 'Parts',
      image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&q=80',
      link: '/parts/stage3'
    },
    {
      title: 'OBD Protection',
      description: 'Accessory',
      image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=800&q=80',
      link: '/parts/obd'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Turkish locale suggestion banner (non-blocking, session-dismissible) */}
      {showTrBanner && (
        <div
          className="fixed top-0 left-0 right-0 z-[70] bg-gradient-to-r from-[#FF6600] to-[#E55A00] text-white h-10 flex items-center px-4 shadow-lg"
          data-testid="tr-locale-banner"
        >
          <Link
            to="/tr/izmir-chip-tuning"
            className="flex-1 text-center text-sm font-medium hover:underline"
            data-testid="tr-banner-link"
          >
            Türkiye'de misiniz? Size özel sayfamıza göz atın →
          </Link>
          <button
            onClick={dismissTrBanner}
            aria-label="Close banner"
            className="text-white/90 hover:text-white hover:bg-white/15 rounded p-1 ml-2 transition"
            data-testid="tr-banner-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <header className={`fixed left-0 right-0 w-full bg-black/95 backdrop-blur-md z-50 border-b border-gray-800 transition-[top] ${showTrBanner ? 'top-10' : 'top-0'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center">
              <img 
                src="/brand/logo.png"
                alt="Powerlab Works"
                className="h-14 w-auto object-contain"
              />
            </a>
            <nav className="hidden md:flex space-x-8">
              <a href="/calculator" className="text-gray-300 hover:text-[#FF6600] transition-colors">Power Calculator</a>
              <a href="#services" className="text-gray-300 hover:text-[#FF6600] transition-colors">Services</a>
              <a href="/faq" className="text-gray-300 hover:text-[#FF6600] transition-colors">FAQ</a>
              <a href="#dealers" className="text-gray-300 hover:text-[#FF6600] transition-colors">Dealers</a>
              <a href="#contact" className="text-gray-300 hover:text-[#FF6600] transition-colors">Contact</a>
            </nav>
            <div className="flex items-center space-x-3">
              <Button 
                onClick={handleWhatsApp}
                className="bg-[#25D366] hover:bg-[#1DA851] text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
              <Button 
                onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=Appointment Request&body=Hello, I would like to book an appointment for chip tuning.'}
                className="bg-[#FF6600] hover:bg-[#E55A00] text-white"
              >
                Book Appointment
              </Button>
              <Button asChild variant="outline" className="border-[#FF6600] text-[#FF6600] hover:bg-[#FF6600] hover:text-white">
                <a href="/dealer/">
                  <Users className="w-4 h-4 mr-2" />
                  Dealer Area
                </a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section with Vehicle Selector */}
      <section className={`relative min-h-screen flex items-center justify-center overflow-hidden ${showTrBanner ? 'pt-28' : 'pt-20'}`}>
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black z-10" />
          <img 
            src="https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1920&q=80" 
            alt="High performance car"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-20 container mx-auto px-4 text-center">
          <div className="mb-12">
            <img 
              src="/brand/logo.png"
              alt="Powerlab"
              className="h-32 w-auto mx-auto mb-8 drop-shadow-2xl"
            />
          </div>
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 tracking-tight">
            Passion<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6600] to-[#E55A00]">High Performance</span>
          </h1>

          {/* Vehicle Search */}
          <Card className="max-w-4xl mx-auto bg-[#1a1a1a]/80 backdrop-blur-lg border-gray-800">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <Select value={selectedBrand} onValueChange={handleBrandChange} disabled={loading}>
                  <SelectTrigger className="bg-black/50 border-gray-700 text-white">
                    <SelectValue placeholder={loading ? "Loading..." : "Select Brand"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {brands.map(brand => (
                      <SelectItem key={brand.id} value={brand.id.toString()}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedModel} onValueChange={handleModelChange} disabled={!selectedBrand || models.length === 0}>
                  <SelectTrigger className="bg-black/50 border-gray-700 text-white">
                    <SelectValue placeholder="Select Model" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {models.map(model => (
                      <SelectItem key={model.id} value={model.id.toString()}>{model.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedGeneration} onValueChange={handleGenerationChange} disabled={!selectedModel || generations.length === 0}>
                  <SelectTrigger className="bg-black/50 border-gray-700 text-white">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {generations.map(gen => (
                      <SelectItem key={gen.id} value={gen.id.toString()}>{gen.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedEngine} onValueChange={setSelectedEngine} disabled={!selectedGeneration || engines.length === 0}>
                  <SelectTrigger className="bg-black/50 border-gray-700 text-white">
                    <SelectValue placeholder="Select Engine" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {engines.map(engine => (
                      <SelectItem key={engine.id} value={engine.id.toString()}>{engine.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={handleSearch}
                className="w-full bg-gradient-to-r from-[#FF6600] to-[#E55A00] hover:from-[#E55A00] hover:to-[#CC4A00] text-white text-lg py-6"
                disabled={!selectedEngine}
              >
                <Search className="mr-2" /> View Performance Data
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Awakening Your Senses Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=800&q=80" 
                alt="Luxury car"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Awakening your senses</span>
              <h2 className="text-4xl font-bold text-white mt-2 mb-6">EXPRESSING YOUR PERSONALITY</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Welcome to Powerlab Works, where we believe that every vehicle tells a story - your story. Our optimization experts transform your machine into a true symbol of your personality.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                Imagine a vehicle that reflects your aesthetic tastes, with performance that exceeds the norm. More than a means of transport, it becomes an extension of yourself.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Your vehicle becomes a manifestation of your success and a testament to your personal distinction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Optimisation Section */}
      <section id="services" className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Optimisation</span>
              <h2 className="text-4xl font-bold text-white mt-2 mb-6">Experience new emotions</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Chiptuning isn't just about increasing power. It's an invitation to rediscover the pure pleasure of driving, to feel that adrenalin rush with every bend, every straight.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                It's a promise to reconnect with those moments when driving was an experience, not a necessity. Treat yourself to this new start, this new adventure. Because driving should never be monotonous.
              </p>
              <Button className="bg-[#FF6600] hover:bg-[#E55A00] text-white">
                Experience Reprogramming <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&q=80" 
                alt="Engine tuning"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Flex-fuel E85 Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80" 
                alt="E85 fuel"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Flex-fuel</span>
              <h2 className="text-4xl font-bold text-white mt-2 mb-6">GREENER, CHEAPER E85</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-4">
                At Powerlab Works, by converting to E85 bioethanol, we're thinking of a greener future where driving pleasure doesn't have to conflict with respect for the environment.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                This solution combines a reduction in your fuel costs with ecological conservation by reducing CO2 emissions. Your vehicle embodies your commitment to a cleaner world, while being more economical.
              </p>
              <Button className="bg-[#FF6600] hover:bg-[#E55A00] text-white">
                Drive on Bioethanol <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* File Service Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-r from-[#FF6600]/10 to-[#E55A00]/10 border-[#FF6600] overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="p-8">
                  <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Professional Service</span>
                  <h2 className="text-4xl font-bold text-white mt-2 mb-6">File Service</h2>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Powerlab Works offers professional ECU file service for tuners worldwide. Upload your original file and receive custom-optimized tuning files developed by our expert engineers.
                  </p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start text-gray-300">
                      <Zap className="w-5 h-5 text-[#FF6600] mr-3 mt-1 flex-shrink-0" />
                      <span>Fast turnaround time - files delivered within hours</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <Zap className="w-5 h-5 text-[#FF6600] mr-3 mt-1 flex-shrink-0" />
                      <span>Custom tuning for every vehicle and customer request</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <Zap className="w-5 h-5 text-[#FF6600] mr-3 mt-1 flex-shrink-0" />
                      <span>Dyno-tested files for optimal performance and safety</span>
                    </li>
                    <li className="flex items-start text-gray-300">
                      <Zap className="w-5 h-5 text-[#FF6600] mr-3 mt-1 flex-shrink-0" />
                      <span>Support for all major ECU types and vehicle brands</span>
                    </li>
                  </ul>
                  <Button 
                    size="lg"
                    className="bg-[#FF6600] hover:bg-[#E55A00] text-white"
                    disabled
                  >
                    Coming Soon - File Service Portal
                  </Button>
                  <p className="text-sm text-gray-500 mt-3">Full file service system launching soon</p>
                </div>
                <div className="relative h-[400px] md:h-full">
                  <img 
                    src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=800&q=80" 
                    alt="File Service"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0a0a0a]" />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Performance Parts */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Performance parts</span>
            <h2 className="text-4xl font-bold text-white mt-2">Take it a step further!</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {performanceParts.map((part, index) => (
              <Card key={index} className="group overflow-hidden bg-[#0a0a0a] border-gray-800 hover:border-[#FF6600] transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={part.image} 
                    alt={part.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-gray-400 text-sm mb-1">{part.description}</p>
                    <h3 className="text-white font-bold mb-2">{part.title}</h3>
                    <a href={part.link} className="text-[#FF6600] text-sm hover:underline">
                      Find out more
                    </a>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Maintenance Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div>
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Maintenance</span>
              <h2 className="text-4xl font-bold text-white mt-2 mb-6">Expertise at your service</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                We offer a high-quality diagnostic and expert service to ensure the performance, reliability and safety of your vehicle.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                Our experienced mechanics carry out a meticulous inspection, identifying potential problems and providing recommendations for the effective maintenance of your vehicle.
              </p>
              <Button className="bg-[#FF6600] hover:bg-[#E55A00] text-white">
                Maintenance by Powerlab <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            <div className="relative h-[500px] rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80" 
                alt="Workshop"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="about" className="py-24 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-[#FF6600] to-[#E55A00]" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-4">
            <img 
              src="/brand/logo.png"
              alt="Powerlab"
              className="h-20 w-auto mx-auto mb-6 drop-shadow-2xl"
            />
            <h3 className="text-2xl font-bold text-white mb-4">Much more than just a brand!</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-[#FF6600] mb-2">
                  {stat.number}
                </div>
                <div className="text-lg text-gray-400 uppercase tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Passion and innovation since 2012</h2>
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              Powerlab Works is the story of a passion that began over a decade ago. We've forged strong links with our customers, sharing much more than just services, but a common passion and real emotions. In addition to our know-how and expertise, it's also our commitment to constant innovation and pushing back the boundaries that has made Powerlab Works a leader in engine optimisation.
            </p>
            <Button className="bg-[#FF6600] hover:bg-[#E55A00] text-white">
              The Powerlab Story <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-white text-center mb-16">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map(testimonial => (
              <Card key={testimonial.id} className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#FF6600] text-[#FF6600]" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t border-gray-800 pt-4">
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">{testimonial.vehicle}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Dealers Section */}
      <section id="dealers" className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-white text-center mb-4">Select a Powerlab center near you</h2>
          <p className="text-xl text-gray-400 text-center mb-16">Our international dealer network</p>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {dealers.map((dealer, index) => (
              <Card key={index} className="bg-black border-gray-800 hover:border-[#FF6600] transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-[#FF6600] mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold text-sm">{dealer.name}</p>
                      <p className="text-gray-500 text-xs">{dealer.city}, {dealer.country}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#FF6600] to-[#E55A00]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">Ready to Unleash Your Vehicle's Potential?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Contact us today to schedule your consultation and experience the Powerlab difference
          </p>
          <div className="flex justify-center space-x-4">
            <Button 
              size="lg" 
              onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=Appointment Request&body=Hello, I would like to book an appointment for chip tuning.'}
              className="bg-black hover:bg-gray-900 text-white px-8 py-6 text-lg"
            >
              Book Appointment
            </Button>
            <Button 
              size="lg" 
              onClick={handleWhatsApp}
              className="bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-6 text-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              WhatsApp Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-black border-t border-gray-900 py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/brand/logo.png"
                alt="Powerlab"
                className="h-14 w-auto object-contain mb-4"
              />
              <p className="text-gray-500 text-sm">Professional chip tuning and ECU remapping since 2012.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-500 text-sm">
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">Chip Tuning</a></li>
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">ECU Remapping</a></li>
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">E85 Conversion</a></li>
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">Performance Parts</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-500 text-sm">
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">Our Dealers</a></li>
                <li><a href="/izmir-chip-tuning" className="hover:text-[#FF6600] transition-colors">Izmir Location</a></li>
                <li><a href="/dealer/" className="hover:text-[#FF6600] transition-colors">Dealer Area</a></li>
                <li><a href="#" className="hover:text-[#FF6600] transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-3 text-gray-500 text-sm">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+905327866635" className="hover:text-[#FF6600] transition-colors">+90 532 786 6635</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>info@powerlab.works</span>
                </li>
                <li className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Tallinn, Estonia</span>
                </li>
                <li>
                  <button 
                    onClick={handleWhatsApp}
                    className="flex items-center space-x-2 text-[#25D366] hover:text-[#1DA851] transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>WhatsApp: +90 532 786 6635</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-900 pt-8 text-center">
            <p className="text-gray-600 text-sm">
              © 2025 Powerlab Works OU. All rights reserved. | As Fast As You Want, As Safe As Original
            </p>
            <LegalLinks lang="en" className="mt-3" />
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button - Right Middle */}
      <a
        href="https://wa.me/905327866635?text=Hello, I would like to know more about Powerlab tuning services"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed top-1/2 right-6 -translate-y-1/2 bg-[#25D366] hover:bg-[#1DA851] text-white p-4 rounded-full shadow-2xl z-50 transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
};

export default Home;